import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { labHorses, getPackage, createJob, errText } from "@/lib/fbLabApi";
import { JobsList } from "./JobsList";

function PackagePreview({ pkg }) {
  return (
    <div data-testid="lab-package" className="border rounded-lg p-3 space-y-2 bg-stone-50">
      <p className="text-xs font-semibold text-stone-500">EXACTLY WHAT WILL BE POSTED · {pkg.photos} photos · {pkg.videos} videos</p>
      {pkg.notes?.map((n) => <p key={n} data-testid="lab-package-note" className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-1.5">{n}</p>)}
      <pre data-testid="lab-package-text" className="whitespace-pre-wrap text-sm font-sans max-h-72 overflow-auto bg-white border rounded p-2">{pkg.ad_text || "(empty)"}</pre>
      <ol data-testid="lab-package-media" className="text-xs space-y-0.5">
        {pkg.media.map((m) => <li key={m.order} className={m.ready ? "" : "text-red-700"}>#{m.order} {m.kind === "video" ? "Video" : "Photo"} — {m.name}{m.ready ? "" : ` — ${m.message}`}</li>)}
      </ol>
      {!pkg.ready && <p data-testid="lab-package-problems" className="text-sm text-red-700">Not ready: {pkg.problems.join("; ")}</p>}
    </div>
  );
}

export function PublishCard({ groupUrl, jobs, onChange }) {
  const [horses, setHorses] = useState([]);
  const [f, setF] = useState({ horse_id: "", mode: "review", confirm_test_group: false });
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { labHorses().then(setHorses).catch(() => {}); }, []);
  useEffect(() => { setPkg(null); }, [f.horse_id]);
  const preview = async () => { setBusy(true); try { setPkg(await getPackage(f.horse_id)); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const queue = async () => {
    setBusy(true);
    try { await createJob(f); toast.success("Queued — the extension picks it up within a minute"); onChange(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="lab-publish" className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
      <h2 className="font-semibold">Publish the auction post</h2>
      <p data-testid="lab-publish-group" className="text-sm text-stone-600">Posts to the Primary Facebook Group: {groupUrl ? <a className="text-[#1877F2] hover:underline break-all" href={groupUrl} target="_blank" rel="noreferrer">{groupUrl}</a> : <span className="text-red-700">not set (Companies → General)</span>}</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Select value={f.horse_id} onValueChange={(v) => setF({ ...f, horse_id: v })}>
          <SelectTrigger data-testid="lab-publish-horse"><SelectValue placeholder="Choose horse" /></SelectTrigger>
          <SelectContent>{horses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name} · {h.photos}p/{h.videos}v{h.is_test ? " · TEST" : ""}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
          <SelectTrigger data-testid="lab-publish-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="review">Review — I click Post myself</SelectItem>
            <SelectItem value="publish">Automatic — extension clicks Post</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="lab-publish-confirm-test" checked={f.confirm_test_group} onCheckedChange={(v) => setF({ ...f, confirm_test_group: !!v })} />This primary group is my private test group (preview testing)</label>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="lab-publish-preview" variant="outline" disabled={!f.horse_id || busy} onClick={preview} className="rounded-full"><Eye className="h-4 w-4 mr-1.5" />Preview what will be posted</Button>
        <Button data-testid="lab-publish-queue" disabled={!pkg?.ready || !groupUrl || !f.confirm_test_group || busy} onClick={queue} className="rounded-full bg-[#1877F2] hover:bg-[#166FE5]"><Send className="h-4 w-4 mr-1.5" />Send to extension</Button>
      </div>
      {pkg && <PackagePreview pkg={pkg} />}
      <JobsList jobs={jobs.filter((j) => j.kind === "publish")} onChange={onChange} />
    </section>
  );
}
